import type { PxlKitData } from '@pxlkit/core';
import {
  Dagger,
  Gem,
  Lightning,
  Medal,
  Potion,
  QuestMap,
  Scroll,
  Skull,
  Star,
  Target,
  Trophy,
} from '@pxlkit/gamification';
import { Camera, Eye, UserGroup } from '@pxlkit/social';
import type { AwardStep, PowerupKind, TaskType, TeamIcon } from '../shared/constants';

/**
 * Halloween pixel icons drawn for Rave Cave in the Pxlkit 16x16 grid format.
 * "." is transparent; every other character maps to the icon's palette.
 */
function pixel(name: string, palette: Record<string, string>, grid: string[]): PxlKitData {
  return { name, size: 16, category: 'rave-cave', grid, palette, tags: [name] };
}

const ghost = pixel('ghost', { W: '#f1ecdf', G: '#c9c2b8', O: '#6b625a', K: '#0b0908' }, [
  '................',
  '......OOOO......',
  '....OOWWWWOO....',
  '...OWWWWWWWWO...',
  '..OWWWWWWWWWWO..',
  '..OWWKKWWKKWWO..',
  '..OWWKKWWKKWWO..',
  '..OWWWWWWWWWWO..',
  '..OWWWWKKWWWWO..',
  '..OWWWWKKWWWGO..',
  '..OWWWWWWWWWGO..',
  '..OWWWWWWWWGGO..',
  '..OWWGWWWGWWGO..',
  '..OWO.OWO.OWGO..',
  '..O...O...OO....',
  '................',
]);

const witch = pixel(
  'witch',
  { H: '#5b4478', R: '#ff4b1f', G: '#8fbf4a', N: '#5f8f2a', K: '#0b0908' },
  [
    '........HH......',
    '.......HHH......',
    '......HHHH......',
    '......HHHHH.....',
    '.....HHHHHH.....',
    '.....RRRRRRR....',
    '..HHHHHHHHHHHH..',
    '....GGGGGGGG....',
    '....GKGGGGKG....',
    '....GGGGNGGG....',
    '....GGGGNNGG....',
    '....GGKKKKGG....',
    '.....GGGGGG.....',
    '................',
    '................',
    '................',
  ],
);

const monster = pixel(
  'monster',
  { K: '#1d1a17', G: '#7fae4a', D: '#4f7a2a', B: '#a79f94', E: '#0b0908' },
  [
    '................',
    '...KKKKKKKKKK...',
    '...KKKKKKKKKK...',
    '...KGKGKGKGKK...',
    '...GGGGGGGGGG...',
    '...GDGDGGGGGG...',
    '...GEEGGGGEEG...',
    '...GGGGGGGGGG...',
    '.BBGGGGDDGGGGBB.',
    '...GGGGGGGGGG...',
    '...GGEEEEEEGG...',
    '...GGGGGGGGGG...',
    '...DDDDDDDDDD...',
    '................',
    '................',
    '................',
  ],
);

const bat = pixel('bat', { B: '#5a4a6a', L: '#7f6d92', E: '#ff4b1f' }, [
  '................',
  '................',
  '......B..B......',
  'B.....BBBB.....B',
  'BB....BEEB....BB',
  'BBB..BBBBBB..BBB',
  'BLBBBBBBBBBBBBLB',
  'BLLBBBBBBBBBBLLB',
  'BLLLBBBBBBBBLLLB',
  'BLLBBBBBBBBBBLLB',
  'BB.B.BBBBBB.B.BB',
  'B.....BBBB.....B',
  '......B..B......',
  '................',
  '................',
  '................',
]);

const pumpkin = pixel(
  'pumpkin',
  { O: '#ff8a1f', D: '#c4501a', S: '#5a7a2a', L: '#8fb33a', K: '#0b0908' },
  [
    '................',
    '........SS......',
    '.......SS.LL....',
    '....DDDSSDDD....',
    '..DDOOODDOOODD..',
    '.DOOOODOODOOOOD.',
    '.DOKKODOODOKKOD.',
    'DOOKOODOODOOKOOD',
    'DOOOODOKKODOOOOD',
    'DOOOODOOOODOOOOD',
    'DOOKKKKKKKKKKOOD',
    'DOOOKKOKKOKKOOOD',
    '.DOOODOOOODOOOD.',
    '..DDOOODDOOODD..',
    '....DDDDDDDD....',
    '................',
  ],
);

const vampire = pixel('vampire', { K: '#1d1a17', W: '#e6e0d4', R: '#c42a1a', E: '#ff4b1f' }, [
  '................',
  '.....KKKKKK.....',
  '....KKKKKKKK....',
  '...KKWWKKWWKK...',
  '...KWWWWWWWWK...',
  '...KWWEWWEWWK...',
  '....WWWWWWWW....',
  '....WKKKKKKW....',
  '....WWKWWKWW....',
  '.RR..WWWWWW..RR.',
  '.RRR..WWWW..RRR.',
  '.RRRR.KKKK.RRRR.',
  '.RRRRKKKKKKRRRR.',
  '.RRRKKKKKKKKRRR.',
  '................',
  '................',
]);

const spider = pixel('spider', { B: '#6b5f58', L: '#8a7e76', E: '#ff4b1f', R: '#ff4b1f' }, [
  '................',
  '...L........L...',
  '....L......L....',
  'L....L.BB.L....L',
  '.L....BEEB....L.',
  '..LLLLBBBBLLLL..',
  '......BBBB......',
  '..LLLBBBBBBLLL..',
  '.L..BBBBBBBB..L.',
  'L..LBBBRRBBBL..L',
  '...LBBBRRBBBL...',
  '..L.BBBBBBBB.L..',
  '.L...BBBBBB...L.',
  '......BBBB......',
  '................',
  '................',
]);

const cat = pixel('cat', { C: '#4a4150', Y: '#dbe23c', K: '#0b0908', P: '#ff8fa0', W: '#a79f94' }, [
  '................',
  '..CC........CC..',
  '..CCC......CCC..',
  '..CCCCCCCCCCCC..',
  '..CCCCCCCCCCCC..',
  '..CCYYCCCCYYCC..',
  '..CCYKCCCCYKCC..',
  '..CCCCCCCCCCCC..',
  '..CCCCCPPCCCCC..',
  'W.CCCCKCCKCCCC.W',
  '...CCCCCCCCCC...',
  '....CCCCCCCC....',
  '...CCCCCCCCCC...',
  '..CCCCCCCCCCCC..',
  '..CCCCCCCCCCCCC.',
  '................',
]);

const owl = pixel('owl', { B: '#8a5a32', L: '#d8b88a', W: '#dbe23c', K: '#0b0908', Y: '#ff8a1f' }, [
  '................',
  '..B..........B..',
  '..BB........BB..',
  '..BBBBBBBBBBBB..',
  '..BWWWBBBBWWWB..',
  '..WWKKWBBWKKWW..',
  '..WWKKWYYWKKWW..',
  '..BWWWBYYBWWWB..',
  '..BBBBBBBBBBBB..',
  '..BBLLLLLLLLBB..',
  '..BBLLBLLBLLBB..',
  '..BBLLLLLLLLBB..',
  '...BBLLLLLLBB...',
  '....BBBBBBBB....',
  '.....Y....Y.....',
  '................',
]);

const moon = pixel('moon', { Y: '#dbe23c', C: '#b8be2a', S: '#9aa020' }, [
  '................',
  '.....YYYYYY.....',
  '...YYYYYYYYYY...',
  '..YYYCCYYYYYYY..',
  '..YYYCCYYYYYYS..',
  '.YYYYYYYYYCCYYS.',
  '.YYYYYYYYYCCYYS.',
  '.YYCYYYYYYYYYYS.',
  '.YYYYYYYYYYYYYS.',
  '.YYYYYCCCYYYYYS.',
  '.YYYYYCCCYYYYSS.',
  '..YYYYYCYYYYSS..',
  '..YYYYYYYYYSSS..',
  '...YYYYYYYSSS...',
  '.....SSSSSS.....',
  '................',
]);

const cobweb = pixel('cobweb', { G: '#c9c2b8' }, [
  'G......G......G.',
  '.G.....G.....G..',
  '..G....G....G...',
  '...GGGGGGGGG....',
  '...GG..G..GG....',
  '...G.G.G.G.G....',
  '...G..GGG..G....',
  'GGGGGGGGGGGGGGG.',
  '...G..GGG..G....',
  '...G.G.G.G.G....',
  '...GG..G..GG....',
  '...GGGGGGGGG....',
  '..G....G....G...',
  '.G.....G.....G..',
  'G......G......G.',
  '................',
]);

const candy = pixel('candy', { P: '#dbe23c', R: '#ff4b1f', W: '#f1ecdf' }, [
  '................',
  '................',
  '................',
  '................',
  '.P............P.',
  '.PP..RRRRRR..PP.',
  '.PPPRRWRRRWRPPP.',
  '.PPPRWRRRWRRPPP.',
  '.PPPRRRRWRRWPPP.',
  '.PPPRRRWRRWRPPP.',
  '.PP..RRRRRR..PP.',
  '.P............P.',
  '................',
  '................',
  '................',
  '................',
]);

const lollipop = pixel('lollipop', { R: '#ff4b1f', W: '#dbe23c', S: '#f1ecdf' }, [
  '................',
  '.....RRRRRR.....',
  '....RWWWWWWR....',
  '...RWRRRRRRWR...',
  '...RWRWWWWRWR...',
  '...RWRWRRWRWR...',
  '...RWRWWRRRWR...',
  '...RWRRRRRRWR...',
  '....RWWWWWWWR...',
  '.....RRRRRRR....',
  '........S.......',
  '........S.......',
  '........S.......',
  '........S.......',
  '........S.......',
  '................',
]);

const bone = pixel('bone', { W: '#f1ecdf', G: '#c9c2b8' }, [
  '................',
  '................',
  '................',
  '................',
  '................',
  '.WW..........WW.',
  'WWWW........WWWW',
  '.WWWWWWWWWWWWWW.',
  '.WWWWWWWWWWWWWG.',
  'WWWW........WWWG',
  '.WG..........GG.',
  '................',
  '................',
  '................',
  '................',
  '................',
]);

const coffin = pixel('coffin', { B: '#7a4a2a', D: '#3b2414', X: '#c9b48a' }, [
  '................',
  '.....DDDDDD.....',
  '....DBBBBBBD....',
  '...DBBBBBBBBD...',
  '..DBBBBXBBBBBD..',
  '..DBBBXXXBBBBD..',
  '..DBBBBXBBBBBD..',
  '..DBBBBXBBBBBD..',
  '...DBBBXBBBBD...',
  '...DBBBBBBBBD...',
  '....DBBBBBBD....',
  '....DBBBBBBD....',
  '.....DBBBBD.....',
  '.....DDDDDD.....',
  '................',
  '................',
]);

const tombstone = pixel('tombstone', { G: '#a79f94', S: '#6b625a', D: '#3b312b', M: '#4a5a2a' }, [
  '................',
  '.....GGGGGG.....',
  '....GGGGGGGG....',
  '...GGGGGGGGGG...',
  '...GGGGDGGGGG...',
  '...GGGDDDGGGG...',
  '...GGGGDGGGGG...',
  '...GGGGDGGGGG...',
  '...GGGGGGGGGS...',
  '...GGGGGGGGGS...',
  '...GGGGGGGGGS...',
  '...GGGGGGGGGS...',
  '..MMMMMMMMMMMM..',
  '.MMMMMMMMMMMMMM.',
  '................',
  '................',
]);

const haunted = pixel(
  'haunted',
  { R: '#6a4058', W: '#7a7068', Y: '#dbe23c', D: '#0b0908', G: '#3a4a24' },
  [
    '................',
    '.......R........',
    '......RRR.......',
    '.....RRRRR......',
    '....RRRRRRR.....',
    '...RRRRRRRRR....',
    '..RRRRRRRRRRR...',
    '...WWWWWWWWW....',
    '...WYYWWWYYW....',
    '...WYYWWWYYW....',
    '...WWWWWWWWW....',
    '...WWWWDDWWW....',
    '...WWWWDDWWW....',
    '...WWWWDDWWW....',
    '.GGGGGGGGGGGGGG.',
    '................',
  ],
);

const broom = pixel('broom', { S: '#a0703a', R: '#ff4b1f', B: '#dbb04a' }, [
  '..............S.',
  '.............S..',
  '............S...',
  '...........S....',
  '..........S.....',
  '.........S......',
  '........S.......',
  '......RR........',
  '.....BBRR.......',
  '....BBBBB.......',
  '...BBBBBB.......',
  '..BBBBBBB.......',
  '.BBBBBBB........',
  'BBBBBB..........',
  'B.B.B...........',
  '................',
]);

const wolf = pixel('wolf', { G: '#7a7480', L: '#c9c2b8', Y: '#dbe23c', K: '#0b0908' }, [
  '................',
  '..G..........G..',
  '..GG........GG..',
  '..GLG......GLG..',
  '..GGGGGGGGGGGG..',
  '..GGGGGGGGGGGG..',
  '..GYKGGGGGGKYG..',
  '..GGGGGGGGGGGG..',
  '...GGGLLLLGGG...',
  '....GGLKKLGG....',
  '....GLLLLLLG....',
  '.....GLLLLG.....',
  '......GLLG......',
  '.......GG.......',
  '................',
  '................',
]);

const crow = pixel('crow', { B: '#3f3850', H: '#5f5878', E: '#dbe23c', Y: '#ff8a1f' }, [
  '................',
  '................',
  '................',
  '......BBB.......',
  '.....BBBBB......',
  '....BBBEBBYY....',
  '....BBBBBBYYY...',
  '...BBBBBB.......',
  '..BBBBBBBB......',
  '.BBBHHHBBBB.....',
  'BBBHHHHBBBB.....',
  'BBHHHHBBBB......',
  '.BBBBBBBB.......',
  '...Y..Y.........',
  '..YY.YY.........',
  '................',
]);

const candle = pixel(
  'candle',
  { F: '#ff4b1f', Y: '#dbe23c', K: '#3b312b', W: '#f1ecdf', S: '#c9c2b8', H: '#a0703a' },
  [
    '................',
    '.......F........',
    '......FFF.......',
    '......FYF.......',
    '......FYF.......',
    '.......K........',
    '.....WWWWW......',
    '.....WWWWS......',
    '.....WWWWS......',
    '....WWWWWS......',
    '.....WWWWS......',
    '.....WWWWS......',
    '.....WWWWS......',
    '...HHHHHHHHH....',
    '....HHHHHHH.....',
    '................',
  ],
);

const mummy = pixel('mummy', { W: '#e8e0c8', G: '#b8ad90', K: '#2a2420', Y: '#dbe23c' }, [
  '................',
  '....WWWWWWWW....',
  '...GGWWWWWWGG...',
  '..WWWWWWGGWWWW..',
  '..GGGWWWWWWWGG..',
  '..KKKKKKKKKKKK..',
  '..KYYKKKKKKYYK..',
  '..KKKKKKKKKKKK..',
  '..WWWWGGWWWWWW..',
  '..GWWWWWWWWGGG..',
  '..WWGGGWWWWWWW..',
  '..WWWWWWWGGWWW..',
  '...WGGWWWWWWW...',
  '....WWWWWWWW....',
  '..........WW....',
  '................',
]);

const cauldron = pixel(
  'cauldron',
  {
    G: '#7fdc3a',
    L: '#c4f07a',
    R: '#6b6468',
    B: '#4a4448',
    H: '#8a8288',
    F: '#ff4b1f',
    Y: '#dbe23c',
  },
  [
    '................',
    '.......L..L.....',
    '.....L...LL.....',
    '...GGGLGGGGGG...',
    '.RRRRRRRRRRRRRR.',
    '..BBBBBBBBBBBB..',
    '.BBHBBBBBBBBBBB.',
    '.BHHBBBBBBBBBBB.',
    '.BHBBBBBBBBBBBB.',
    '.BBBBBBBBBBBBBB.',
    '..BBBBBBBBBBBB..',
    '...BBBBBBBBBB...',
    '...B..FFFF..B...',
    '..BB.FFYYFF.BB..',
    '.....FFFFFF.....',
    '................',
  ],
);

/** Team and player icons, keyed by the stored icon name. */
export const gameIcons: Record<TeamIcon, PxlKitData> = {
  ghost,
  witch,
  monster,
  skull: Skull,
  bat,
  pumpkin,
  vampire,
  spider,
  cat,
  owl,
  moon,
  crystal: Gem,
  cobweb,
  candy,
  lollipop,
  bone,
  coffin,
  tombstone,
  haunted,
  broom,
  wolf,
  crow,
  potion: Potion,
  candle,
  mummy,
  cauldron,
};

/** Retired icon keys that older player and team records may still hold. */
export const legacyAvatarIcons: Record<string, PxlKitData> = {
  frankenstein: monster,
  scream: cauldron,
  mask: mummy,
};

/** Award art; `tint` recolours the shared medal for silver and bronze. */
export const awardIcons: Record<AwardStep, { icon: PxlKitData; tint?: string }> = {
  third: { icon: Medal, tint: '#cd7f32' },
  second: { icon: Medal, tint: '#c0c0c0' },
  individual: { icon: Star },
  first: { icon: Trophy },
};

export const taskTypeIcons: Record<TaskType, PxlKitData> = {
  qr: Target,
  photo: Camera,
  manual: UserGroup,
  trivia: Scroll,
};

const shotGlass = pixel('shot-glass', { G: '#e8e2d8', A: '#ff8a1f', W: '#fff4c8' }, [
  '................',
  '................',
  '...GGGGGGGGGG...',
  '...G........G...',
  '...GWAAAAAAAG...',
  '....GWAAAAAG....',
  '....GWAAAAAG....',
  '....GAAAAAAG....',
  '....GAAAAAAG....',
  '.....GAAAAG.....',
  '.....GAAAAG.....',
  '.....GGGGGG.....',
  '.....GGGGGG.....',
  '................',
  '................',
  '................',
]);

export const powerupIcons: Record<PowerupKind, PxlKitData> = {
  steal: Dagger,
  peek: Eye,
  hint: QuestMap,
  dare: shotGlass,
};

/** Shown while a team's first-finisher double-points boost is active. */
export const boostIcon = Lightning;
